/**
 * Getting someone back to the deck they were trying to open.
 *
 * Hosted decks live at `/p/<slug>/`, which the API serves itself - outside the
 * SPA's routes and outside `/api` - because a deck is 199 files of mp4 and jpg
 * rather than a JSON document. A signed-out visitor who follows a deck link is
 * therefore bounced by the API, not by the router, and it bounces them to
 * `/?next=%2Fp%2F<slug>%2F`. This is the other half of that handshake: once the
 * session is real, follow `next` and leave.
 *
 * Three things about it are deliberate.
 *
 * It lives here rather than on the login page. `app.tsx` renders `<LoginPage/>`
 * *in place of* the router while signed out, so the login page never unmounts
 * into a route - the moment the session lands, `App` simply re-renders with the
 * real app. That re-render is the only reliable place to notice we owe someone
 * a redirect, and it is also the one that works when the person was already
 * signed in and arrived with `?next=` in hand.
 *
 * It reads the query string once, when this module is first evaluated, and not
 * inside the effect. That is not tidiness, it is the whole thing working: the
 * index route redirects to `/leaderboard` with a `replace`, React runs a child's effects before its parent's, and so by the
 * time an effect in `App` looks at `window.location` the router has already
 * rewritten it and `?next=` is gone. Reading at module scope happens before
 * React mounts anything, which is the one moment the URL is still the one the
 * browser arrived with. This was a real bug and the symptom was undramatic:
 * you followed a deck link, signed in, and landed on the board wondering what
 * you had clicked.
 *
 * It is a full navigation, not `navigate()`. `/p/...` is not a route this
 * router has; a client-side push would swap the URL and render nothing.
 *
 * And it follows only our own deck paths. This is the single place in the app
 * that takes a URL from a query string and goes there, which is exactly the
 * shape of an open redirect, so `isSafeDeckRedirect` is the whole allowlist:
 * one leading slash, `/p/` and nothing clever. A `next` it refuses is dropped
 * in silence - the person is signed in and looking at the app, which is a fine
 * outcome for a link somebody tampered with.
 */
import { useEffect } from "react";
import { isSafeDeckRedirect } from "@scandi/shared";

/**
 * Where a followed redirect is remembered for the rest of the tab's life.
 *
 * Insurance against a loop: if the deck bounces us straight back - the cookie
 * expired in the seconds between, the role was taken away, the row was deleted
 * - we would otherwise ping-pong between the API and the app forever. Having
 * tried once, we stay put and let the person read whatever the portal says.
 */
const FOLLOWED_KEY = "scandi.deckRedirect";

/**
 * @param ready Whether the session is one a deck would actually accept: signed
 * in *and* holding a role. Somebody still waiting for access is left on the
 * "no access yet" screen with `?next=` untouched in the URL, so the redirect
 * happens by itself the moment an admin lets them in and the session refetches.
 */
export function useDeckRedirect(ready: boolean): void {
  useEffect(() => {
    if (!ready) return;
    const next = REQUESTED_DECK;
    if (next === null) return;
    if (alreadyFollowed(next)) return;
    remember(next);
    window.location.replace(next);
  }, [ready]);
}

/**
 * The deck this page load was asked for, decided once and kept.
 *
 * Null both when there was no `next` and when there was one we will not follow,
 * because the two lead to exactly the same thing: stay where you are. Wrapped
 * in a try because a malformed query string is not worth a blank screen.
 */
const REQUESTED_DECK: string | null = (() => {
  if (typeof window === "undefined") return null;
  try {
    const next = new URLSearchParams(window.location.search).get("next");
    return isSafeDeckRedirect(next) ? next : null;
  } catch {
    return null;
  }
})();

/* `sessionStorage` is absent or throws in a locked-down browser, and a missing
   loop guard is a far smaller problem than refusing to open the deck, so both
   helpers fail open. */

function alreadyFollowed(next: string): boolean {
  try {
    return window.sessionStorage.getItem(FOLLOWED_KEY) === next;
  } catch {
    return false;
  }
}

function remember(next: string): void {
  try {
    window.sessionStorage.setItem(FOLLOWED_KEY, next);
  } catch {
    /* Nothing to do: the redirect still happens, it just is not guarded. */
  }
}
